import { createSelector } from 'reselect';
import includes from 'lodash/includes';
import some from 'lodash/some';
import get from 'lodash/get';

import {
  doGuestRequireBed,
} from './rules';
import { INFANT } from '../../domain/age';

export const initialState = {
  departures: {},
  guests: {},
  cabins: {},
  vehicles: {},
  activeCabin: {},
  replacedGuests: {},
  applicationFeatures: {},
};

export const getState = state => state.reservation || initialState;

export const getSalesOwnerId = state => state.salesOwnerId;

export const getId = (state = initialState) => state.id;

export const getReservationStatus = (state = initialState) => state.reservationStatus;

export const getApplicationFeatures = (state = initialState) => state.applicationFeatures;

export const hasReservationFailed = (state = initialState) => {
  const status = getReservationStatus(state);
  const toCompare = status ? status.toLowerCase() : status;
  return toCompare === 'expired' || toCompare === 'cancelled';
};

export const getDeparture = (state = initialState, id = 'out') => state.departures[id] || {};
export const getDepartures = (state = initialState) => state.departureIds || [];
export const getActiveDepartureId = (state = initialState) => state.activeDeparture || '';
export const getActiveCabin = (state = initialState, departureId = '') => state.activeCabin[departureId] || '';
export const getDepartureWithCabin = (state = initialState, cabinId) =>
  Object.keys(state.departures).find(id => state.departures[id].cabins.find(id => id === cabinId));

export const getAgesForDeparture = (state = initialState, departureId) => getDeparture(state, departureId).validCustomerCategories || [];

export const getGuestId = (state = initialState, id = '') => {
  if (!state.replacedGuests) {
    return id;
  }
  return state.replacedGuests[id] || id;
};

export const getGuestIds = (state = initialState) => Object.keys(state.guests) || [];
export const getGuest = (state = initialState, id = '') => (state.guests || {})[getGuestId(state, id)] || {};
export const getGuests = (state = initialState) => state.guests || {};
export const getCabinIds = (state = initialState) => Object.keys(state.cabins);
export const getCabin = (state = initialState, id = '') => state.cabins[id] || {};
export const getDepartuerAndCabinObject = (state = initialState, id = '') => {
  if (id instanceof Array) {
    const createCabinObject = id.map((singleId, i) => {
      const departure = getDepartures(state)[i];
      const cabinId = state.cabins[singleId.toString()].id;
      const departureAndCabinId = { [departure]: cabinId };
      return departureAndCabinId;
    });
    const arrayToObject = Object.assign({}, ...createCabinObject);
    return arrayToObject;
  }
  return null;
};
export const getVehicleIds = (state = initialState) => Object.keys(state.vehicles);
export const getVehicle = (state = initialState, id = '') => state.vehicles[id] || {};

export const getVisaRequiredCountriesPerDeparture = (state = initialState) => getDeparture(state, getActiveDepartureId(state)).visaRequired || [];

export const getVisaRequiredForGuest = (state = initialState, id) => getVisaRequiredCountriesPerDeparture(state).includes(getGuest(state, id).nationality);

export const getNumberOfVisaRequired = (state = initialState) => getGuestIds(state).filter(id => getVisaRequiredForGuest(state, id)).length;

export const getValidationErrors = (state = initialState) =>
  getGuestIds(state)
    .map(id => ({
      ...getGuest(state, id),
      id,
    }))
    .map(guest => ({
      guestId: guest.id,
      numberOf: get(guest, 'validationFieldsNotCompleted', []).length,
      fields: get(guest, 'validationFieldsNotCompleted', []),
    }))
    .filter(o => o.numberOf > 0);

export const getRequiredInputFields = (state = initialState) =>
  getGuestIds(state)
    .map(id => ({
      ...getGuest(state, id),
      id,
    }))
    .map(guest => ({
      guestId: guest.id,
      numberOf: get(guest, 'requiredFieldsNotCompleted', []).length,
      fields: get(guest, 'requiredFieldsNotCompleted', []),
    }))
    .filter(o => o.numberOf > 0);

export const getResponseFromServer = (state = initialState) =>
  getGuestIds(state)
    .map(id => ({
      ...getGuest(state, id),
      id,
    }))
    .filter(guest => get(guest, 'validationStatus.numberOfRequiredFields', 0) > 0)
    .map(guest => guest.validationStatus);

export const getCabinByGuest = (state = initialState, guestId) => getGuest(state, guestId).selectedCabin || {};

export const getServerSaveStatus = (state = initialState) => state.serverSaveStatus || {};

export const getSaveStatus = (state = initialState) => state.responseFromServer || {};
export const getGuestSaveStatus = (state = initialState, guestId) => getGuest(state, guestId).saveStatus || '';
export const getCabinSaveStatus = (state = initialState, guestId, outOrHome) => {
  const getSpecificCabinFromObject = outOrHome === 'out' ? 0 : 1;
  const cabinId = Object.values(getCabinByGuest(state, guestId))[getSpecificCabinFromObject];
  return (state.cabins[cabinId] && state.cabins[cabinId].saveStatus) || '';
};

export const getHeaderRequiredFieldToggle = (state = initialState) => state.headerRequiredFieldIsOpen || false;

export const isCabinsAllocated = (state = initialState) =>
  getDepartures(state).map(id => ({
    ...getDeparture(state, id),
    id,
  })).filter(dep => dep.cabinsAllocated === true).length > 0 || false;

export const isCheckInStarted = (state = initialState) =>
  getDepartures(state).map(id => ({
    ...getDeparture(state, id),
    id,
  })).filter(dep => dep.passengersHaveBeenCheckedIn === true).length > 0 || false;

export const collectIcePhoneNumber = (state = initialState) =>
  getDepartures(state).map(id => ({
    ...getDeparture(state, id),
    id,
  })).filter(dep => dep.collectIcePhoneNumber === true).length > 0 || false;

export const collectLoyaltyNumber = (state = initialState) => {
  const applicationFeatures = getApplicationFeatures(state);
  return applicationFeatures ? applicationFeatures.collectLoyaltyNumber : true;
};

export const collectPassportInfo = (state = initialState) =>
  getDepartures(state).map(id => ({
    ...getDeparture(state, id),
    id,
  })).filter(dep => dep.collectPassportInfo === true).length > 0 || false;

export const getGuestsByDeparture = (state = initialState, departureId) => getDeparture(state, departureId).guests || [];
export const getGuestsByCabin = (state = initialState, cabinId) => getCabin(state, cabinId).guests || [];

export const getCabinsByDeparture = (state = initialState, departureId) => getDeparture(state, departureId).cabins || [];
export const getCabinByDepartureAndGuest = (state = initialState, departureId, guestId) =>
  (getGuest(state, guestId).selectedCabin || {})[departureId] || '';

export const getVehiclesByDeparture = (state = initialState, departureId) => getDeparture(state, departureId).vehicles || [];

export const isAllGuestsExpanded = (state = initialState) =>
  Object.keys(state.guests).map(id => getGuest(state, id)).reduce((agg, curr) => agg && curr.expanded, true);

export const getPayingCustomer = (state = initialState) => state.payingCustomer || {};

export const getReservationCode = (state = initialState) => state.reservationCode || '';

export const getLanguageCode = (state = initialState) => state.languageCode || '';

export const getImportedFileData = (state = initialState) => state.importedFile ? state.importedFile.data : [] || [];

export const getImportedFileTitleRow = (state = initialState) => state.importedFile || [];

export const getImportedFileTitleOnColumnNumber = (state = initialState) => state.importedFile ? state.importedFile.titleOnColumnNumber || [] : [];

export const getImportedFileStepProcess = (state = initialState) => state.importedFileStepProcess ? state.importedFileStepProcess.stepsProcess || [] : [];

export const getImportedFileActiveStep = (state = initialState) => state.importedFileStepProcess ? state.importedFileStepProcess.activeStep || 0 : 0;

export const getActiveCabinInCabinDistributor = (state = initialState) => state.importedFile ? state.importedFile.activeCabinInCabinDistributor || '' : '';

export const getActivePersonInCabinDistributor = (state = initialState) => state.importedFile ? state.importedFile.activePersonInCabinDistributor || '' : '';

export const getPersonLinkedToCabin = (state = initialState) => state.importedFile ? state.importedFile.personLinkedToCabin || [] : [];

export const getIStenaTemplate = (state = initialState) => state.importedFile ? state.importedFile.stenaTemplate || false : false;

export const getAnonymousIdAndCabinForImport = (state = initialState, id) => state.importedFile ? state.importedFile.anonymousIdAndCabinForImport.map(a => a[id]).filter(a => a).toString() || [] : [];

export const isBirthDateMandatory = (state = initialState) =>
  some(getDepartures(state).map(id => getDeparture(state, id).birthDateMandatory || false));

export const getAgesForActiveCabin = createSelector(
  state => state,
  getActiveDepartureId,
  (state, departureId) => {
    const cabinId = getActiveCabin(state, departureId);
    const guestIdsInCabin = getGuestsByCabin(state, cabinId);
    const bedsInCabin = getCabin(state, cabinId).noOfBerths;
    const requiredBeds = guestIdsInCabin.map(id => getGuest(state, id)).filter(doGuestRequireBed).length;
    if (requiredBeds < bedsInCabin) {
      return getAgesForDeparture(state, departureId).map(age => age.code);
    }
    return [INFANT];
  },
);
export const canGuestBeAddedToActiveCabin = (state, guestId) => {
  const guest = getGuest(state, guestId);
  const validAges = getAgesForActiveCabin(state);
  return includes(validAges, guest.customerCategory);
};

export const getRemainingBeds = (state, cabinId) =>
  (getCabin(state, cabinId).noOfBerths || 0)
  - getGuestsByCabin(state, cabinId).map(id => getGuest(state, id)).filter(doGuestRequireBed).length;

export const getRemainingBeds1 = (state, cabinId) => (getCabin(state, cabinId).noOfBerths || 0);

export const isCabinFull = (state, cabinId) => getRemainingBeds(state, cabinId) === 0;

export const getEmptyPassportFields = (state, guestId) => {
  const {
    documentType,
    documentNumber,
    documentIssuingCountry,
    documentExpiryDate,
  } = getGuest(state, guestId);

  if (collectPassportInfo) {
    const passportValues = [
      { field: 'documentType', value: documentType },
      { field: 'documentNumber', value: documentNumber },
      { field: 'documentIssuingCountry', value: documentIssuingCountry },
      { field: 'documentExpiryDate', value: documentExpiryDate },
    ];

    const emptyValues = passportValues.filter(item => item.value === '' || !item.value);

    return emptyValues;
  }
  return [];
};

export const getMissingPassportFields = (state, guestId) => {
  const emptyValues = getEmptyPassportFields(state, guestId);

  if (emptyValues.length > 0 && emptyValues.length < 4) {
    return emptyValues;
  }
  return [];
};

export const isPassportFieldsComplete = (state, guestId) => {
  const emptyValues = getEmptyPassportFields(state, guestId);

  if (emptyValues.length > 0 && emptyValues.length < 4) {
    return false;
  }
  return true;
};

export const isGuestsRequiredFieldsFilled = (state, guestId) => {
  const {
    firstName,
    lastName,
    gender,
    customerCategory,
    dateOfBirth,
    nationality,
    requiredFieldsNotCompleted = [],
    validationFieldsNotCompleted = [],
  } = getGuest(state, guestId);

  const validGuest = (!!firstName && !!lastName && !!gender && !!customerCategory && !!nationality && (isBirthDateMandatory(state) ? !!dateOfBirth : true) && requiredFieldsNotCompleted.length === 0 && validationFieldsNotCompleted.length === 0) && isPassportFieldsComplete(state, guestId);

  return validGuest;
};

export const scoreCabinBasedOnRemainingBeds = (state, cabinId) =>
  (getCabin(state, cabinId).noOfBerths || 0) - getGuestsByCabin(state, cabinId).length;

export const isAllGuestsInCabin = (state, departureId) =>
  getGuestsByDeparture(state, departureId)
    .filter(id => getCabinByDepartureAndGuest(state, departureId, id) === '').length === 0;

export const getAllRemainingCabinsIdsWithoutGuests = state => Object.keys(state.cabins).map(id => state.cabins[id].guests.length === 0 ? id : null).filter(a => a) || [];

export const isAllCabinsPopulated = (state, departureId) =>
  getCabinsByDeparture(state, departureId).map(id => getCabin(state, id)).filter(cabin => cabin.guests.length === 0).length === 0;

export const getGuestNumberOnDeparture = (state, guestId, departureId) =>
  get(getGuest(state, guestId), `number.${departureId}`, 0);

export const getDepartureRouteName = (state, departureId) => (state && state.departures[departureId] && state.departures[departureId].routeName) || '';



// WEBPACK FOOTER //
// ./src/store/reservation/selectors.js